// 드롭 식별 모델(순수 로직, DOM·three import 금지): 바닥 이름표(render/lootLabels.ts)와 줍기 알림 띠(lootFeed.ts)가 같은 이름·종류·그림·등급색을 쓴다.
// 대표님 지적(2026-10-27) "아이템이 장비인지 재료인지 한눈에": 종류(장비·재료·보석)와 등급색을 이름과 함께 보인다.
import { GRADES, NAMED_COLOR } from '../shared/content/items';
import { STONE_TIERS } from '../shared/content/economy';
import { GEM_COLORS, gemName, isGemCode } from '../shared/content/consumables';
import type { Grade } from '../shared/content/types';
import { activeSets, equipDef, itemName, type GearItem } from '../shared/gear';
import { SLOT_ORDER } from '../shared/gearLedger';
import { gearCompare, gearIconPath, SLOT_KO } from './gearModel';
import { consIconPath, gearDefIconCandidates, gemIconPath, gradeBadgePath } from './itemIcons';

/** 드롭 종류(LootKind·Ev.LootPickup a와 같은 번호) */
export const DropKind = { Gear: 0, Stone: 1, Gem: 2 } as const;
/** 종류 태그 문구 */
export const KIND_TAG: readonly string[] = ['장비', '재료', '보석'];
/** 종류 그림(public/assets/ui/items/cat_*.webp, 2026-10-27 아이콘 회차) */
export const KIND_ICON: readonly string[] = ['ui/items/cat_gear.webp', 'ui/items/cat_material.webp', 'ui/items/cat_gem.webp'];
/** 종류 태그 색(등급색과 따로 읽히게 낮은 채도): 장비 금빛, 재료 청회, 보석 청록 */
export const KIND_TAG_COLOR: readonly string[] = ['#e3c27a', '#9fc3e6', '#7fe0d0'];

export const GRADE_BY_INDEX: readonly Grade[] = ['common', 'rare', 'epic', 'set', 'legend'];
/** 등급 번호 → CSS 색(GRADES 정본) */
export function gradeCssByIndex(i: number): string { return GRADES[Math.max(0, Math.min(4, i | 0))].color; }
/** 등급 번호 → 이름 */
export function gradeNameByIndex(i: number): string { return GRADES[Math.max(0, Math.min(4, i | 0))].name; }

/** 드롭 하나의 표시 정보(이름표·띠 공용). 객체는 이벤트·드롭 단위로 한 번 만들고 프레임마다 만들지 않는다 */
export interface DropInfo {
  readonly kind: number;
  /** 이름(장비: 이름, 강화석: '강화석(하)', 보석: '상급 루비') */
  readonly name: string;
  /** 종류 태그('장비'·'재료'·'보석') */
  readonly tag: string;
  /** 글자색(등급색, 강화석 청회, 보석 그 색) */
  readonly color: string;
  /** 등급 번호(0..4, 장비만 의미 있음) */
  readonly grade: number;
  /** 아이템 그림 후보(앞에서부터 시도, assets/ 아래 상대 경로) */
  readonly icons: readonly string[];
  /** 부위 이름(장비만, 아니면 '') */
  readonly slotName: string;
}

/** 장비 하나 → 표시 정보 */
export function gearDropInfo(item: GearItem | null, gradeIndex: number, slotIdx: number): DropInfo {
  const g = Math.max(0, Math.min(4, gradeIndex | 0));
  const d = item ? equipDef(item.def) : undefined;
  const slotName = d ? SLOT_KO[d.slot] : slotIdx >= 0 && slotIdx < SLOT_ORDER.length ? SLOT_KO[SLOT_ORDER[slotIdx]] : '장비';
  const icons = item ? gearIcons(item) : [];
  // 이름 있는 유니크(item.nu)는 등급(영웅) 보라 대신 주황(NAMED_COLOR) — 바닥 빛기둥(render/loot.ts 칸 5)과 같은 색
  return { kind: DropKind.Gear, name: item ? itemName(item) : GRADES[g].name + ' ' + slotName, tag: KIND_TAG[0], color: item?.nu ? NAMED_COLOR : GRADES[g].color, grade: g, icons, slotName };
}
/** 강화석 → 표시 정보 */
export function stoneDropInfo(tier: number, count: number): DropInfo {
  const t = STONE_TIERS[Math.max(0, Math.min(STONE_TIERS.length - 1, tier | 0))];
  const p = consIconPath(t.id);
  return { kind: DropKind.Stone, name: t.name + (count > 1 ? ' ×' + count : ''), tag: KIND_TAG[1], color: '#9fc3e6', grade: 0, icons: p ? [p] : [], slotName: '' };
}
/** 보석 → 표시 정보(code = 보석 코드, colorIdx = 색 번호) */
export function gemDropInfo(code: number, colorIdx: number): DropInfo {
  const ci = Math.max(0, Math.min(GEM_COLORS.length - 1, colorIdx | 0));
  const p = isGemCode(code) ? gemIconPath(code) : null;
  return { kind: DropKind.Gem, name: isGemCode(code) ? gemName(code) : '보석', tag: KIND_TAG[2], color: GEM_COLORS[ci].color, grade: 0, icons: p ? [p] : [], slotName: '' };
}

/** 장비 그림 후보: 드롭·세트·상점 전용 그림(등급 그림 → 일반 그림) → 부위×단계 그림 */
export function gearIcons(item: GearItem): readonly string[] {
  const c = gearDefIconCandidates(item.def, item.grade);
  return c.length ? [...c, gearIconPath(item)] : [gearIconPath(item)];
}
/** 아이콘 모서리 배지(세트·전설) */
export function gradeBadge(gradeIndex: number): string | null { return gradeBadgePath(GRADE_BY_INDEX[Math.max(0, Math.min(4, gradeIndex | 0))]); }

// ── 장착 중인 장비와 비교 ────────────────────────────────
/** 비교 결과: 0 비교 없음(다른 영웅 무기 등) 1 장착 중보다 좋음 2 같거나 낮음·엇갈림 3 빈 칸(새로 낄 수 있음) */
export const Compare = { None: 0, Better: 1, NotBetter: 2, EmptySlot: 3 } as const;
/** 세트 효과 점수: 장착한 세트마다 켜진 2·4세트 효과 수의 합(장비 창 setRows와 같은 activeSets 판정) */
function setScore(items: readonly (GearItem | null)[]): number {
  let n = 0;
  for (const a of activeSets(items)) n += (a.on2 ? 1 : 0) + (a.on4 ? 1 : 0);
  return n;
}
/**
 * 방금 주운 장비 vs 장착 중인 같은 부위 장비 — 가방 화면의 비교 열(gearModel.gearCompare: 기본 능력치·강화·옵션 값)과 같은 기준에 세트 효과를 더한다.
 * 줄이 하나라도 오르고(▲) 내려가는 줄(▼)이 없을 때만 Better(엇갈리면 NotBetter: 가방 화면에서 직접 고르게 한다). 세트 효과는 장착 세트의 켜진 2·4세트 효과 수가 늘면 ▲, 줄면 ▼ 한 줄로 센다.
 * 무기는 지금 영웅 전용일 때만 비교한다(다른 영웅 무기는 None). cur = 장착 중 같은 부위 장비(없으면 null = 빈 칸), others = 장착 중인 나머지 부위(세트 효과용, 없으면 [])
 */
export function compareToEquipped(item: GearItem, cur: GearItem | null, heroId: string | undefined, others: readonly (GearItem | null)[] = []): number {
  const d = equipDef(item.def);
  if (!d) return Compare.None;
  if (d.heroLock && heroId !== undefined && d.heroLock !== heroId) return Compare.None;
  if (!cur) return Compare.EmptySlot;
  let up = 0, down = 0;
  for (const r of gearCompare(item, cur)) { if (r.up) up++; else down++; }
  if (others.length > 0) {
    const sd = setScore([...others, item]) - setScore([...others, cur]);
    if (sd > 0) up++; else if (sd < 0) down++;
  }
  return up > 0 && down === 0 ? Compare.Better : Compare.NotBetter;
}
